import { useCallback, useEffect, useMemo, useState } from 'react';
import { type OpenApiSchemaAlert } from '#shared/api/schema';
import { DASHBOARD_ALERTS_COOLDOWN_KEY, usePersistentStorage } from '#shared/lib/storage';

/** Повторно напоминаем о неустранённых критичных алертах через три часа. */
export const DASHBOARD_ALERTS_PRESENTATION_THRESHOLD_MS = 3 * 60 * 60 * 1000;

type CooldownUntilByAlertKey = Record<string, number>;

function getAlertKey(alert: OpenApiSchemaAlert): string {
    return `${alert.id}:${alert.created_at}`;
}

function parseCooldownUntilByAlertKey(rawValue: string | null): CooldownUntilByAlertKey {
    if (rawValue === null) {
        return {};
    }

    try {
        const value: unknown = JSON.parse(rawValue);

        if (typeof value !== 'object' || value === null || Array.isArray(value)) {
            return {};
        }

        return Object.fromEntries(
            Object.entries(value).filter((entry): entry is [string, number] => {
                const cooldownUntil = entry[1];

                return typeof cooldownUntil === 'number' && Number.isFinite(cooldownUntil);
            })
        );
    } catch {
        return {};
    }
}

type UseDashboardAlertsCooldownResult = Readonly<{
    criticalAlerts: readonly OpenApiSchemaAlert[];
    hasAlertReadyToShow: boolean;
    startCriticalAlertsCooldown: VoidFunction;
}>;

/**
 * Хранит cooldown отдельно для каждого появления критичного алерта (`id + created_at`)
 * и инициирует пересчёт после истечения ближайшего cooldown.
 *
 * @kind helper
 * @param alerts Загруженные активные алерты; `undefined` означает, что запрос ещё не завершён.
 * @returns Критичные алерты, их готовность к показу и действие для сохранения cooldown.
 */
export function useDashboardAlertsCooldown(
    alerts: readonly OpenApiSchemaAlert[] | undefined
): UseDashboardAlertsCooldownResult {
    const [cooldownUntilRaw, setCooldownUntilRaw] = usePersistentStorage(DASHBOARD_ALERTS_COOLDOWN_KEY);
    const [currentTime, setCurrentTime] = useState(Date.now);

    const criticalAlerts = useMemo(() => alerts?.filter(({ severity }) => severity === 'red') ?? [], [alerts]);
    const cooldownUntilByAlertKey = useMemo(() => parseCooldownUntilByAlertKey(cooldownUntilRaw), [cooldownUntilRaw]);
    const hasAlertReadyToShow = criticalAlerts.some(
        (alert) => (cooldownUntilByAlertKey[getAlertKey(alert)] ?? 0) <= currentTime
    );

    const startCriticalAlertsCooldown = useCallback(() => {
        if (criticalAlerts.length === 0) {
            return;
        }

        const closedAt = Date.now();
        const nextCooldownUntilByAlertKey = Object.fromEntries(
            Object.entries(cooldownUntilByAlertKey).filter(([, cooldownUntil]) => cooldownUntil > closedAt)
        );
        const nextPresentationAt = closedAt + DASHBOARD_ALERTS_PRESENTATION_THRESHOLD_MS;

        criticalAlerts.forEach((alert) => {
            nextCooldownUntilByAlertKey[getAlertKey(alert)] = nextPresentationAt;
        });

        setCooldownUntilRaw(JSON.stringify(nextCooldownUntilByAlertKey));
    }, [cooldownUntilByAlertKey, criticalAlerts, setCooldownUntilRaw]);

    useEffect(() => {
        if (criticalAlerts.length === 0) {
            return;
        }

        const now = Date.now();
        const nextPresentationAt = Math.min(
            ...criticalAlerts.map((alert) => cooldownUntilByAlertKey[getAlertKey(alert)] ?? now)
        );

        if (nextPresentationAt <= now) {
            return;
        }

        const timeoutId = setTimeout(() => setCurrentTime(Date.now()), nextPresentationAt - now);

        return () => clearTimeout(timeoutId);
    }, [cooldownUntilByAlertKey, criticalAlerts]);

    return { criticalAlerts, hasAlertReadyToShow, startCriticalAlertsCooldown };
}
